import React, { useContext, useEffect, useReducer, useState } from "react";
import { Badge, Form, Modal } from "react-bootstrap";
import Button from "react-bootstrap/Button";
import Col from "react-bootstrap/Col";
import FormControl from "react-bootstrap/FormControl";
import InputGroup from "react-bootstrap/InputGroup";
import Row from "react-bootstrap/Row";
import DatePicker from "react-date-picker";
import { Helmet } from "react-helmet-async";
import { useMediaQuery } from "react-responsive";
import { toast } from "react-toastify";
import { Store } from "../Store";
import ExpenseEditor from "../components/ExpenseEditor";
import FiliskaPagination from "../components/FiliskaPagination";
import LoadingBox from "../components/LoadingBox";
import MessageBox from "../components/MessageBox";
import useAxios from "../hooks/useAxios";
import {
  expenseTypes,
  getError,
  getExpenseSubTypeLabel,
  getExpenseSubTypes,
  getExpenseTypeLabel,
  round2,
  turkishCurrencyFormatter,
  turkishCurrencyFormatterNoPrecision,
} from "../utils";
import ExpenseChart from "./ExpenseChart";
import StockInputListScreen from "./StockInputListScreen";

const reducer = (state, action) => {
  switch (action.type) {
    case "SET_SORTED_EXPENSE":
      return { ...state, expenses: action.payload };
    case "FETCH_REQUEST":
      return { ...state, loading: true, error: "" };
    case "FETCH_SUCCESS":
      return {
        ...state,
        expenses: action.payload.expenses,
        expensesTypeGrouped: (action.payload.expensesTypeGrouped || []).map(
          (exp) => ({ ...exp, type: getExpenseTypeLabel(exp.type) })
        ),
        expensesSubTypeGrouped:
          action.payload.expensesSubTypeGrouped?.map((v) => ({
            ...v,
            _id: {
              ...v._id,
              subType: getExpenseSubTypeLabel(v._id.type, v._id.subType),
            },
          })) || [],
        totalPrice: action.payload.totalPrice,
        totalCachePrice: action.payload.totalCachePrice,
        totalPosPrice: action.payload.totalPosPrice,
        page: action.payload.page,
        pages: action.payload.pages,
        loading: false,
        error: "",
      };
    case "FETCH_FAIL":
      return { ...state, loading: false, error: action.payload };
    case "UPDATE_REQUEST":
      return { ...state, loadingUpdate: true };
    case "UPDATE_SUCCESS":
      return { ...state, loadingUpdate: false };
    case "UPDATE_FAIL":
      return { ...state, loadingUpdate: false };
    default:
      return state;
  }
};

export default function ExpenseListScreen(props) {
  const RADIAN = Math.PI / 180;
  const renderCustomizedLabel = ({
    cx,
    cy,
    midAngle,
    innerRadius,
    outerRadius,
    name,
    index,
  }) => {
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
      <text
        x={x}
        y={y}
        fill="#222"
        fontSize={15}
        fontWeight={"bold"}
        textAnchor={x > cx ? "start" : "end"}
        dominantBaseline="central"
      >
        {`${
          getExpenseTypeLabel(name) +
          " (" +
          expensesTypeGrouped[index].totalPrice +
          ") "
        }`}
      </text>
    );
  };

  const axios = useAxios();
  const [
    {
      error,
      expenses,
      expensesTypeGrouped,
      expensesSubTypeGrouped,
      totalPrice,
      totalCachePrice,
      totalPosPrice,
      pages,
      loading,
      loadingUpdate,
    },
    dispatch,
  ] = useReducer(reducer, {
    loading: false,
    error: "",
    expenses: [],
    expensesTypeGrouped: [],
    expensesSubTypeGrouped: [],
    totalPrice: 0,
    loadingUpdate: false,
    totalCachePrice: 0,
    totalPosPrice: 0,
  });

  const isTabletOrMobile = useMediaQuery({ query: "(max-width: 1224px)" });
  const [page, setPage] = useState(1);
  const [pageSize] = useState(props.pageSize || 4);
  const [tarihAsc, setTarihAsc] = useState(props.tarihAsc ?? null);
  const [miktarDesc, setMiktarDesc] = useState(props.miktarDesc ?? null);

  const { state } = useContext(Store);
  const { userInfo } = state;
  const [query, setQuery] = useState(null);
  const [queryStartDate, setQueryStartDate] = useState(
    new Date(new Date().getFullYear(), 0, 1)
  );
  const [queryEndDate, setQueryEndDate] = useState(null);
  const [reportStartDate, setReportStartDate] = useState(props.queryStartDate);
  const [reportEndDate, setReportEndDate] = useState(props.queryEndDate);
  const [showExpenseEditor, setShowExpenseEditor] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState({});
  const [forReport] = useState(props.forReport || false);

  const [total, setTotal] = useState(null);
  const [type, setType] = useState("");
  const [subType, setSubType] = useState("");
  const [description, setDescription] = useState("");
  const [paymentType, setPaymentType] = useState("CACHE");
  const [installment, setInstallment] = useState(false);
  const [expenseId, setExpenseId] = useState("");
  const [subTypes, setSubTypes] = useState([]);
  const [installments, setInstallments] = useState([{ amount: null }]);
  const [queryByDesc, setQueryByDesc] = useState("");
  const [queryType, setQueryType] = useState("");
  const [queryStockOnly, setQueryStockOnly] = useState(false);
  const [queryInstallment, setQueryInstallment] = useState(false);
  const [querySubType, setQuerySubType] = useState("");
  const [querySubTypes, setQuerySubTypes] = useState([]);
  const [showFilter, setShowFilter] = useState(false);
  const [showStockEditor, setShowStockEditor] = useState(false);
  const [stock, setStock] = useState({});
  const [stockInputSuccessfull, setStockInputSuccessfull] = useState(false);

  const fetchData = async () => {
    dispatch({ type: "FETCH_REQUEST" });

    try {
      if (reportStartDate || reportEndDate) {
        let firstDate = new Date(reportStartDate);
        let secondDate = new Date(reportEndDate);

        const { data } = await axios.get(
          `/api/expense/search?page=${page}&pageSize=${pageSize}&byDesc=${queryByDesc}&byOnlyStok=${queryStockOnly}&byInstallment=${queryInstallment}&byType=${queryType}&bySubType=${querySubType}&firstDate=${firstDate.toISOString()}&secondDate=${secondDate.toISOString()}&tarihAsc=${tarihAsc}&miktarDesc=${miktarDesc}`
        );
        dispatch({ type: "FETCH_SUCCESS", payload: data });
      } else if (query || queryStartDate || queryEndDate) {
        let firstDate;
        let secondDate;
        if (queryStartDate && !queryEndDate) {
          firstDate = new Date(queryStartDate);
          secondDate = new Date();
          secondDate.setDate(secondDate.getDate() + 1);
        }
        if (queryEndDate && !queryStartDate) {
          let millenium = new Date();
          millenium.setYear(2020, 1, 1);
          firstDate = millenium;
          secondDate = new Date(queryEndDate);
          secondDate.setDate(secondDate.getDate() + 1);
        }
        if (queryStartDate && queryEndDate) {
          firstDate = new Date(queryStartDate);
          secondDate = new Date(queryEndDate);
          secondDate.setDate(secondDate.getDate() + 1);
        }
        if (!queryStartDate && !queryEndDate && query) {
          secondDate = new Date(query);
          secondDate.setDate(secondDate.getDate() + 1);
          firstDate = new Date(query);
        }
        const { data } = await axios.get(
          `/api/expense/search?page=${page}&pageSize=${pageSize}&byDesc=${queryByDesc}&byOnlyStok=${queryStockOnly}&byInstallment=${queryInstallment}&byType=${queryType}&bySubType=${querySubType}&year1=${firstDate.getFullYear()}&month1=${firstDate.getMonth()}&day1=${firstDate.getDate()}&year2=${secondDate.getFullYear()}&month2=${secondDate.getMonth()}&day2=${secondDate.getDate()}&tarihAsc=${tarihAsc}&miktarDesc=${miktarDesc}`
        );
        dispatch({ type: "FETCH_SUCCESS", payload: data });
      } else {
        const { data } = await axios.get(
          `/api/expense?page=${page}&pageSize=${pageSize}&byDesc=${queryByDesc}&byOnlyStok=${queryStockOnly}&byInstallment=${queryInstallment}&byType=${queryType}&bySubType=${querySubType}&tarihAsc=${tarihAsc}&miktarDesc=${miktarDesc}`
        );
        dispatch({ type: "FETCH_SUCCESS", payload: data });
      }
    } catch (err) {
      dispatch({ type: "FETCH_FAIL", payload: err.message });
      toast.error(getError(err));
    }
  };

  const editStockHandler = (stock) => {
    setStock(stock);
    setShowStockEditor(true);
  };

  const expenseHandler = async (e) => {
    e.preventDefault();
    try {
      if (!type) {
        toast.error("Lutfen gider türü giriniz");
        return;
      }
      if (!subType) {
        toast.error("Lutfen gider alt türü giriniz");
        return;
      }

      if (!total || total === 0) {
        if (installment) {
          toast.error("Lutfen toplam ödenecek tutarı giriniz");
        } else {
          toast.error("Lutfen tutar giriniz");
        }
        return;
      }

      if (installment) {
        const filtered = installments.filter((ins) => !ins.amount);
        if (filtered && filtered.length > 0) {
          toast.error("Lutfen taksit tutarlarını giriniz");
          return;
        }
      }

      if (installment) {
        const totalInstallment = installments.reduce((a, c) => a + c.amount, 0);
        if (totalInstallment > total) {
          toast.error(
            "Taksit tutarlarinin toplami toplam ödenecek miktarı geçemez"
          );
          return;
        }
      }

      dispatch({ type: "UPDATE_REQUEST" });
      const { data } = expenseId
        ? await axios.put(`/api/expense/${expenseId}`, {
            total: round2(Number(total)),
            description,
            type,
            subType,
            paymentType,
            installment,
            installments,
          })
        : await axios.post(`/api/expense`, {
            total: round2(Number(total)),
            description,
            type,
            subType,
            paymentType,
            installment,
            installments,
          });

      setTotal("");
      setDescription("");
      setType("");
      setSubType("");
      setPaymentType("CACHE");
      setInstallment(false);
      setInstallments([{ amount: null }]);
      setExpenseId("");
      setSubTypes([]);
      setShowExpenseEditor(false);
      setQuery(null);

      dispatch({
        type: "UPDATE_SUCCESS",
        payload: data,
      });
      fetchData();
      toast.success("Gider basarili bir sekilde kaydedildi");
    } catch (err) {
      setShowExpenseEditor(false);
      toast.error(getError(err));
      dispatch({ type: "UPDATE_FAIL" });
    }
  };

  useEffect(() => {
    setReportStartDate(props.queryStartDate);
    setReportEndDate(props.queryEndDate);
  }, [props.queryStartDate, props.queryEndDate]);

  useEffect(() => {
    fetchData();
  }, [
    page,
    userInfo,
    query,
    queryByDesc,
    reportStartDate,
    miktarDesc,
    tarihAsc,
  ]);

  useEffect(() => {
    if (stockInputSuccessfull) {
      fetchData();
      setStockInputSuccessfull(false);
    }
  }, [stockInputSuccessfull]);

  const deleteExpenseHandler = async (expenseId) => {
    if (window.confirm("Silmek istediginize emin misiniz?")) {
      try {
        await axios.delete(`/api/expense/${expenseId}`);

        toast.success("Gider silindi!");
        fetchData();
      } catch (error) {
        toast.error(getError(error));
      }
    }
  };

  const deleteStockHandler = async (stockId) => {
    if (window.confirm("Silmek istediginize emin misiniz?")) {
      try {
        await axios.delete(`/api/stock/inputStock/${stockId}`);

        toast.success("Stok silindi!");
        fetchData();
      } catch (error) {
        toast.error(getError(error));
      }
    }
  };

  if (error) {
    return (
      <div className="small-container">
        <MessageBox variant="danger">{error}</MessageBox>
      </div>
    );
  }

  return (
    <div>
      {!forReport && (
        <Helmet>
          <title>Giderler</title>
        </Helmet>
      )}

      {!isTabletOrMobile && !forReport && (
        <>
          <h1 className="my-3 text-muted">Giderler</h1>
          <hr className="hr"></hr>
        </>
      )}

      {!forReport && (
        <div className="hstack gap-3 mb-3">
          <div className="ms-auto">
            <Button
              type="button"
              onClick={() => {
                setSelectedExpense({
                  _id: 0,
                  description: "",
                  type: "",
                  subType: "",
                  total: "",
                  paymentType: "CACHE",
                  installment: false,
                  installments: [{ amount: null }],
                });
                setExpenseId("");
                setSubTypes([]);
                setPaymentType("CACHE");
                setInstallment(false);
                setInstallments([{ amount: null }]);
                setShowExpenseEditor(true);
              }}
              className={"text-primary border-primary"}
              variant="light"
            >
              <i className="fas fa-plus"></i>
              {!isTabletOrMobile ? <>&nbsp; Yeni Gider</> : <>&nbsp; G</>}
            </Button>
          </div>
          <div>
            <Button
              type="button"
              onClick={() => {
                setShowStockEditor(true);
                editStockHandler({
                  price: "",
                  amount: "",
                  feature: "",
                  stockId: "",
                  paymentType: "CACHE",
                });
              }}
              className={"text-success border-success"}
              variant="light"
            >
              <i className="fas fa-plus"></i>
              {!isTabletOrMobile ? <>&nbsp; Yeni Stok</> : <>&nbsp; S</>}
            </Button>
          </div>
        </div>
      )}

      {error ? (
        <MessageBox variant="danger">{error}</MessageBox>
      ) : (
        <div className="row">
          <div className="col-md-6">
            <table className="table table-striped">
              <thead>
                <tr>
                  <th colSpan={2}>
                    <InputGroup>
                      <FormControl
                        type="text"
                        value={queryByDesc}
                        onChange={(e) => {
                          if (!loading) {
                            setQueryByDesc(e.target.value);
                            setPage(1);
                          }
                        }}
                        placeholder="Ara..."
                        aria-label="Ara"
                      ></FormControl>
                      {!forReport && (
                        <Button
                          variant="light"
                          className="border-primary text-primary"
                          onClick={() => setShowFilter(true)}
                        >
                          <i className="fa fa-filter"></i>
                          {!isTabletOrMobile && <>&nbsp; Filtre Ekle</>}
                        </Button>
                      )}
                      {!forReport &&
                        (queryType ||
                          querySubType ||
                          queryByDesc ||
                          queryStockOnly ||
                          queryInstallment ||
                          queryStartDate ||
                          queryEndDate) && (
                          <Button
                            variant="light"
                            className="border-danger text-danger"
                            onClick={async () => {
                              setQueryType("");
                              setQuerySubType("");
                              setQueryByDesc("");
                              setQueryStockOnly(false);
                              setQueryStartDate(null);
                              setQueryEndDate(null);
                              setQueryInstallment(false);
                              try {
                                const { data } = await axios.get(
                                  `/api/expense?page=${page}&pageSize=${pageSize}`
                                );
                                dispatch({
                                  type: "FETCH_SUCCESS",
                                  payload: data,
                                });
                                setShowFilter(false);
                              } catch (err) {
                                toast.error(getError(err));
                              }
                            }}
                          >
                            Temizle
                          </Button>
                        )}
                    </InputGroup>
                  </th>
                  {forReport && <th>Harcamalar</th>}
                  <th
                    colSpan={forReport ? 1 : 2}
                    style={{ textAlign: "right" }}
                  >
                    <div>
                      {tarihAsc ? (
                        <Button
                          variant="light"
                          className={
                            tarihAsc === null
                              ? "text-secondary p-1"
                              : "text-primary p-1"
                          }
                          onClick={() => {
                            setTarihAsc(false);
                            setMiktarDesc(null);
                          }}
                        >
                          Tar<i className="fas fa-caret-up"></i>
                        </Button>
                      ) : (
                        <Button
                          variant="light"
                          className={
                            tarihAsc === null
                              ? "text-secondary p-1"
                              : "text-danger p-1"
                          }
                          onClick={() => {
                            if (tarihAsc === null) {
                              setTarihAsc(false);
                              setMiktarDesc(null);
                            } else {
                              setTarihAsc(true);
                              setMiktarDesc(null);
                            }
                          }}
                        >
                          Tar<i className="fas fa-caret-down"></i>
                        </Button>
                      )}
                      &nbsp;
                      {miktarDesc ? (
                        <Button
                          variant="light"
                          className={
                            miktarDesc === null
                              ? "text-secondary p-1"
                              : "text-danger p-1"
                          }
                          onClick={() => {
                            if (miktarDesc === null) {
                              setTarihAsc(null);
                              setMiktarDesc(true);
                            } else {
                              setTarihAsc(null);
                              setMiktarDesc(false);
                            }
                          }}
                        >
                          Mik<i className="fas fa-caret-down"></i>
                        </Button>
                      ) : (
                        <Button
                          variant="light"
                          className={
                            miktarDesc === null
                              ? "text-secondary p-1"
                              : "text-primary p-1"
                          }
                          onClick={() => {
                            setTarihAsc(null);
                            setMiktarDesc(true);
                          }}
                        >
                          Mik<i className="fas fa-caret-up"></i>
                        </Button>
                      )}
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {((!loading && !expenses) || expenses.length === 0) && (
                  <tr>
                    <td>Harcama Yok!</td>
                  </tr>
                )}
                {!loading &&
                  expenses.map((expense) => (
                    <tr key={expense._id}>
                      <td>
                        <div>
                          <div>
                            {expense.description && (
                              <div>
                                {expense.description}
                                {expense.stockInput && (
                                  <>
                                    &nbsp;
                                    <span className="text-muted">
                                      {turkishCurrencyFormatter(
                                        expense.total /
                                          Number(
                                            expense.description.substring(
                                              0,
                                              expense.description.indexOf(" ")
                                            )
                                          )
                                      )}
                                    </span>
                                  </>
                                )}
                                {expense.installment && (
                                  <>
                                    &nbsp;
                                    <Badge pill bg="info">
                                      Taksitli
                                    </Badge>
                                  </>
                                )}
                              </div>
                            )}
                          </div>
                          <span className="text-muted">
                            {getExpenseSubTypeLabel(
                              expense.type,
                              expense.subType
                            )}{" "}
                            - {getExpenseTypeLabel(expense.type)}{" "}
                            {expense.stockInput ? (
                              <Badge pill bg="dark">
                                Stok Girişi
                              </Badge>
                            ) : null}
                          </span>
                        </div>
                      </td>
                      <td>
                        {turkishCurrencyFormatterNoPrecision(
                          expense.installment
                            ? expense.paidAmount
                            : expense.total
                        )}{" "}
                        <span className="text-danger">
                          {expense.paymentType === "POS" ? "POS" : "Nakit"}
                        </span>
                        <div>
                          <span className="text-muted">
                            {new Date(expense.createdAt)
                              .toLocaleString("tr-TR")
                              .substring(0, 16)}
                          </span>
                        </div>
                      </td>

                      <td>
                        {expense.editable && (
                          <>
                            <Button
                              variant="light"
                              className="text-success p-1"
                              onClick={() => {
                                if (expense.stockInput) {
                                  editStockHandler(expense.stockInput);
                                } else {
                                  setSelectedExpense(expense);
                                  setShowExpenseEditor(true);
                                  setExpenseId(expense._id);
                                  setSubTypes(getExpenseSubTypes(expense.type));
                                }
                              }}
                            >
                              <i className="fas fa-edit"></i>
                            </Button>
                            &nbsp;
                            <Button
                              variant="light"
                              className="text-danger p-1"
                              onClick={() =>
                                expense.stockInput
                                  ? deleteStockHandler(expense.stockInput._id)
                                  : deleteExpenseHandler(expense._id)
                              }
                            >
                              <i className="fas fa-trash"></i>
                            </Button>
                          </>
                        )}
                        {!expense.editable && (
                          <div>
                            <span className="text-muted small-font">
                              İşlendi
                            </span>
                            &nbsp;
                            <i className="fa fa-check text-success"></i>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                {loading && (
                  <tr>
                    <td colSpan={10}>
                      <LoadingBox></LoadingBox>
                    </td>
                  </tr>
                )}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={4}>
                    <FiliskaPagination
                      leftRightSize={2}
                      page={page}
                      pages={pages}
                      onFirst={() => setPage(1)}
                      onLast={() => setPage(pages)}
                      onNext={() => {
                        if (page < pages) {
                          setPage(page + 1);
                        }
                      }}
                      onPrev={() => {
                        if (page > 1) {
                          setPage(page - 1);
                        }
                      }}
                      onItemNext={(p) => setPage(p)}
                      onItemPrev={(p) => setPage(p)}
                    ></FiliskaPagination>
                  </td>
                </tr>
              </tfoot>
            </table>

            {!loading && expenses && expenses.length > 0 && (
              <>
                <table className="table ">
                  <tbody>
                    <tr>
                      <td className="text-bold">
                        <span>Toplam Harcama &nbsp;</span>
                      </td>
                      <td className="text-end">
                        {turkishCurrencyFormatterNoPrecision(totalPrice)}
                      </td>
                    </tr>
                    <tr>
                      <td className="text-bold">
                        <span>Nakit</span>{" "}
                      </td>
                      <td className="text-end">
                        {turkishCurrencyFormatterNoPrecision(totalCachePrice)}
                      </td>
                    </tr>
                    <tr>
                      <td className="text-bold">
                        <span>POS</span>{" "}
                      </td>
                      <td className="text-end">
                        {turkishCurrencyFormatterNoPrecision(totalPosPrice)}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </>
            )}
          </div>
          <div className="col-md-6">
            <div
              style={{
                width: "100%",
                height: "450px",
              }}
            >
              <ExpenseChart
                expensesTypeGrouped={expensesTypeGrouped}
                expensesSubTypeGrouped={expensesSubTypeGrouped}
                renderCustomizedLabel={renderCustomizedLabel}
              />
            </div>
          </div>
        </div>
      )}

      <Modal
        backdrop="static"
        show={showExpenseEditor}
        onHide={() => setShowExpenseEditor(false)}
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {!selectedExpense || !selectedExpense._id
              ? "Gider Oluştur"
              : "Gider Güncelle"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <ExpenseEditor
            expense={selectedExpense}
            total={total}
            type={type}
            subType={subType}
            description={description}
            paymentType={paymentType}
            setTotal={setTotal}
            setType={setType}
            setSubType={setSubType}
            setDescription={setDescription}
            setPaymentType={setPaymentType}
            installment={installment}
            installments={installments}
            setInstallment={setInstallment}
            setInstallments={setInstallments}
            expenseHandler={expenseHandler}
            subTypes={subTypes}
            setSubTypes={setSubTypes}
          ></ExpenseEditor>
        </Modal.Body>
        <Modal.Footer>
          <Button disabled={loadingUpdate} onClick={expenseHandler}>
            {expenseId ? "Guncelle" : "Kaydet"}
          </Button>
          {loadingUpdate && <LoadingBox></LoadingBox>}
          <Button
            variant="secondary"
            onClick={() => setShowExpenseEditor(false)}
          >
            Kapat
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showFilter} onHide={() => setShowFilter(false)}>
        <Modal.Header>
          <Modal.Title>Filtre Ekle</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Check
              type="checkbox"
              label="Yanlız Stokları Göster?"
              value={queryStockOnly}
              checked={queryStockOnly}
              onChange={(e) => {
                setQueryStockOnly(e.target.checked);
              }}
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Check
              type="checkbox"
              label="Yanlız Taksitli Olanları Göster?"
              value={queryInstallment}
              checked={queryInstallment}
              onChange={(e) => {
                setQueryInstallment(e.target.checked);
              }}
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Başlama Tarihi</Form.Label>
            <div>
              <DatePicker
                value={queryStartDate}
                locale="tr-TR"
                closeCalendar={false}
                onChange={(d) => {
                  setQueryStartDate(d);
                }}
                className="text-center flex-auto"
              ></DatePicker>
            </div>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Bitiş Tarihi</Form.Label>
            <div>
              <DatePicker
                value={queryEndDate}
                locale="tr-TR"
                closeCalendar={false}
                onChange={(d) => {
                  setQueryEndDate(d);
                }}
                className="text-center flex-auto"
              ></DatePicker>
            </div>
          </Form.Group>
          <Form.Group className="mb-3">
            <Row>
              <Col md={9}>
                <Form.Select
                  value={queryType}
                  onChange={(e) => {
                    setQueryType(e.target.value);
                    setQuerySubTypes(getExpenseSubTypes(e.target.value));
                  }}
                >
                  <option value={""}>Gider türü...</option>
                  {expenseTypes.map((c) => (
                    <option value={c.type} key={c.type}>
                      {c.name}
                    </option>
                  ))}
                </Form.Select>
              </Col>
            </Row>
          </Form.Group>
          {querySubTypes.length > 0 && (
            <Form.Group className="mb-3">
              <Row>
                <Col md={9}>
                  <Form.Select
                    value={querySubType}
                    onChange={(e) => {
                      setQuerySubType(e.target.value);
                    }}
                  >
                    <option value={""}>Gider alt türü</option>
                    {querySubTypes.map((c) => (
                      <option value={c.type} key={c.type}>
                        {c.name}
                      </option>
                    ))}
                  </Form.Select>
                </Col>
              </Row>
            </Form.Group>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowFilter(false)}>
            Kapat
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              try {
                fetchData();
                setShowFilter(false);
                setPage(1);
              } catch (err) {
                toast.error(getError(err));
              }
            }}
          >
            Filtrele
          </Button>
        </Modal.Footer>
      </Modal>

      <div style={{ display: "none" }}>
        <StockInputListScreen
          stockFromExpense={stock}
          setStockInputSuccessfull={setStockInputSuccessfull}
          showEditor={showStockEditor}
          setShowStockEditor={setShowStockEditor}
          expenseMod={true}
        ></StockInputListScreen>
      </div>
    </div>
  );
}
